import { watch } from "vue";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";

import axios from "@/api/axios";

import { clearUserSession, getUserData } from "@/stores/localStorage.js";

import useProfileStore from "../../../stores/profile.store";

import { useAuthStore } from "@/stores/auth.store";
import { makeValidator } from "@/plugins/simple-body-validator/helpers";

import { toast } from "@/shared/utils/swalUtils";

const rawApiUrl = import.meta.env.VITE_API_URL ?? axios.defaults.baseURL ?? "";
const API_URL = rawApiUrl.replace(/\/+$/, "");

const buildProviderUrl = (provider) => {
  if (!API_URL) {
    throw new Error(
      "[Auth] VITE_API_URL no está definida. El login SSO requiere una URL absoluta del backend."
    );
  }
  return `${API_URL}/auth/${provider}/redirect?platform=web`;
};

const fetchCSRFCookie = async () => {
  const response = await axios.get("/sanctum/csrf-cookie");
  return response;
};

const authenticate = async (credentials) => {
  const { data } = await axios.post("/login", credentials);
  return data;
};

const deauthenticate = async () => {
  const { data } = await axios.post(`/logout`);
  return data;
};

const getCurrentUser = async () => {
  const { data } = await axios.get("/user");
  return data;
};

const getProviders = async () => {
  const { data } = await axios.get("/auth/providers");
  return data;
};

const checkAuthentication = async () => {
  try {
    const profileStore = useProfileStore();
    const data = await getCurrentUser();

    profileStore.setUserData(data?.data);

    const store = useAuthStore();

    const { avatar } = storeToRefs(store);

    avatar.value = data?.data?.avatar;

    return { isLogged: true };
  } catch (error) {
    return { isLogged: false };
  }
};

const useAuthenticate = () => {
  const router = useRouter();

  const store = useAuthStore();
  const { credentials, avatar } = storeToRefs(store);

  const { validate, errors, setErrors } = makeValidator(credentials);

  const queryClient = useQueryClient();

  const socialAuth = {
    google: import.meta.env.VITE_GOOGLE === 'true',
    microsoft: import.meta.env.VITE_MICROSOFT === 'true',
  };

  // Login SSO
  const loginWithProvider = (provider) => {
    try {
      const redirect = router.currentRoute.value.query?.redirect;
      if (redirect) sessionStorage.setItem("lastPath", redirect);

      window.location.href = buildProviderUrl(provider);
    } catch (error) {
      console.error(error);
      toast("error", "Ha habido un error, intente mas tarde.", "bottom-right");
    }
  };

  const handleProviderCallback = async () => {
    const error = router.currentRoute.value.query?.error;

    if (error) {
      toast("error", error, "bottom-right");
      router.replace({ name: "auth-login" });
      return;
    }

    const { isLogged } = await checkAuthentication();

    if (isLogged) {
      toast("success", "¡Sesión iniciada exitósamente!", "bottom-right");
      queryClient.invalidateQueries();

      const lastPath = sessionStorage.getItem("lastPath");
      sessionStorage.removeItem("lastPath");
      router.replace(lastPath || { name: "dashboard" });
    } else {
      toast("error", "No se pudo iniciar sesión", "bottom-right");
      router.replace({ name: "auth-login" });
    }
  };

  // CSRF
  useQuery({
    queryKey: ["csrf-cookie"],
    queryFn: fetchCSRFCookie,
  });

  // Login
  const {
    mutate: loginMutation,
    isPending: loginIsPending,
    isError: loginIsError,
    data: loginData,
    isSuccess: loginIsSuccess,
  } = useMutation({
    mutationFn: () => authenticate(credentials.value),
    onSuccess: () => {
      toast("success", "¡Sesión iniciada exitósamente!", 'bottom-right');
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      setErrors(error);
    },
  });

  // Logout
  const { mutate: logout } = useMutation({
    mutationFn: deauthenticate,
    onSuccess: () => {
      clearUserSession();
      queryClient.clear();
      sessionStorage.removeItem("lastPath");
      router.push({ name: "auth-login" });
    },
  });

  const login = async () => {
    if (!validate()) return;
    loginMutation();
  };

  watch(
    loginIsSuccess,
    () => {
      if (loginIsSuccess.value === true) {
        const lastPath = sessionStorage.getItem("lastPath");
        router.push(lastPath || { name: "dashboard" });

        store.setCurrentUser(loginData.data);
      }
    },
    { immediate: true }
  );

  return {
    //* Properties
    credentials,

    //* Methods
    loginIsPending,
    loginIsError,
    loginIsSuccess,

    errors,
    avatar,

    socialAuth,

    login,
    logout,
    loginWithProvider,
    handleProviderCallback,
    getProviders,
  };
};

export {
  checkAuthentication,
  useAuthenticate,
  getUserData,
  //getUserRoles,
  //hasRoles,
  //hasPermission,
};
